import React, {useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {OverlayPanel} from "primereact/overlaypanel";
import {ProgressBar} from "primereact/progressbar";
import {useSubscriptionStatus} from "../../../models/subscription/subscription-status-store";
import {bannerOf, elapsedPercent, endDateText, hasCountdown, indicatorOf, remainingText} from "./subscription-status-labels";
import "./subscription-status.css";

// «وضعیت اشتراک» (boss's task «نمایش وضعیت اشتراک کاربر در فضای کاری», 2026-11-01; docs/tasks/36-subscription-status.md)
// - SubscriptionStatusIndicator: always in the sidebar, small; opens the existing subscription page in settings for
//   people who manage billing, and a short note for everyone else.
// - SubscriptionStatusBanner: above the page only when action is needed; never flashing; a mild one can be closed and
//   comes back at the next threshold, a critical one cannot be closed.

export function SubscriptionStatusIndicator({workspace, expanded}) {
    const navigate = useNavigate();
    const panel = useRef(null);
    const {status, error, loading} = useSubscriptionStatus(workspace);
    const view = indicatorOf(status);
    if (!workspace) return null;
    if (loading && !status) return <div className={`ss-indicator ss-loading ${expanded ? "" : "ss-compact"}`} aria-hidden="true"/>;
    if (error && !status) return null; // no made-up state on a failed request; the settings page still works
    if (!view) return null;
    const open = (event) => {
        if (status.canManage) navigate(status.managePath || "/settings?tab=subscriptions");
        else panel.current?.toggle(event);
    };
    return <>
        <button type="button" className={`ss-indicator ss-${view.tone} ${expanded ? "" : "ss-compact"}`} onClick={open}
                aria-label={`وضعیت اشتراک: ${view.text}`} title={expanded ? undefined : view.text}>
            <i className={`pi ${view.icon}`} aria-hidden="true"/>
            {expanded && <span className="ss-text">{view.text}</span>}
        </button>
        <OverlayPanel ref={panel} className="rtl shabnam-font" pt={{content: () => ({className: "p-3"})}}>
            <div className="flex flex-column gap-2 text-xs" style={{maxWidth: "16rem"}}>
                <strong className="text-sm text-900">{view.text}</strong>
                {status.periodEnd && !["none", "suspended"].includes(status.state) && <span className="text-600">پایان اعتبار: {endDateText(status.periodEnd)}{status.remaining && status.state !== "expired" ? ` · ${remainingText(status.remaining)}` : ""}</span>}
                {status.cancelAtPeriodEnd && <span className="text-600">تمدید خودکار خاموش است؛ تا تاریخ پایان فعال می‌ماند.</span>}
                <span className="text-600 line-height-3">برای خرید یا تمدید با مدیر فضای کاری تماس بگیرید.</span>
            </div>
        </OverlayPanel>
    </>;
}

const DISMISS_STORE = "zofino:subscription-banner-dismissed";
const readDismissed = () => {
    try { return JSON.parse(window.localStorage.getItem(DISMISS_STORE) || "{}"); } catch { return {}; }
};

export function SubscriptionStatusBanner({workspace}) {
    const navigate = useNavigate();
    const {status} = useSubscriptionStatus(workspace);
    const banner = bannerOf(status);
    const [dismissed, setDismissed] = useState(readDismissed);
    if (!banner) return null;
    const key = banner.dismissKey ? `${workspace}:${banner.dismissKey}` : null;
    if (!banner.critical && key && dismissed[key]) return null;
    const dismiss = () => {
        const next = {...readDismissed(), [key]: Date.now()};
        try { window.localStorage.setItem(DISMISS_STORE, JSON.stringify(next)); } catch { /* the banner just closes for now */ }
        setDismissed(next);
    };
    return <div className={`ss-banner ss-banner-${banner.tone} flex flex-column sm:flex-row sm:align-items-center gap-2 px-3 py-2`} role={banner.critical ? "alert" : "status"}>
        <i className={`pi ${banner.tone === "info" ? "pi-info-circle" : "pi-exclamation-triangle"} flex-shrink-0`} aria-hidden="true"/>
        <span className="flex-1 text-xs line-height-3">{banner.message}</span>
        <span className="flex align-items-center gap-2 flex-shrink-0">
            {banner.action && <Button type="button" size="small" className="text-xs" label={banner.action} onClick={() => navigate(status.managePath || "/settings?tab=subscriptions")}/>}
            {!banner.critical && key && <Button type="button" text size="small" className="text-xs p-1" icon="pi pi-times" aria-label="بستن این یادآوری" onClick={dismiss}/>}
        </span>
    </div>;
}

// Home dashboard card (boss's task «کاربران روز های باقی مانده اشتراک خود را بتوانند مشاهده کنند», docs/tasks/40):
// the plan, how many days (hours on the last day) are left, the end date and how much of the period has passed.
// Warning colors in the last 7 days; the renew / manage action only for people who manage billing.
const CARD_TONE = {ok: "text-green-600", info: "text-primary", warn: "text-orange-500", danger: "text-red-500", neutral: "text-500"};
export function SubscriptionHomeCard({workspace}) {
    const navigate = useNavigate();
    const {status, loading} = useSubscriptionStatus(workspace);
    const view = indicatorOf(status);
    if (!workspace || (loading && !status) || !status || !view) return null;
    if (status.state === "active" && !hasCountdown(status)) return null; // nothing to count down
    if (["none", "suspended"].includes(status.state) && !status.canManage) return null;
    const elapsed = elapsedPercent(status);
    const ending = ["expiring", "trial_expiring"].includes(status.state);
    const ended = ["expired", "grace", "payment_failed"].includes(status.state);
    const headline = status.remaining && !ended && status.state !== "none" ? remainingText(status.remaining) : view.text;
    const action = status.canManage
        ? (ended || ending ? (status.trial ? "انتخاب پلن" : "تمدید اشتراک") : "مدیریت اشتراک")
        : null;
    return <section data-home-section="subscription" aria-label="اشتراک فضای کاری"
                    className={`surface-card border-round-xl shadow-2 p-3 md:px-4 flex flex-column md:flex-row md:align-items-center gap-3 ${ending ? "border-1 border-orange-200" : ended ? "border-1 border-red-200" : ""}`}>
        <span className={`w-3rem h-3rem border-round-xl surface-100 flex align-items-center justify-content-center flex-shrink-0 ${CARD_TONE[view.tone] || ""}`}>
            <i className={`pi ${view.icon} text-xl`} aria-hidden="true"/>
        </span>
        <div className="flex-1 min-w-0 flex flex-column gap-2">
            <div className="flex flex-wrap align-items-center gap-2">
                <small className="text-xs text-500">{status.trial ? "دورهٔ آزمایشی" : `اشتراک ${status.planName || ""}`.trim()}</small>
                <strong className={`text-base ${CARD_TONE[view.tone] || "text-900"}`}>{headline}</strong>
            </div>
            {status.periodEnd && !["none", "suspended"].includes(status.state) && (
                <span className="text-xs text-500">{ended ? "پایان اعتبار" : "اعتبار تا"} {endDateText(status.periodEnd)}{status.cancelAtPeriodEnd ? " · تمدید خودکار خاموش است" : ""}</span>
            )}
            {elapsed !== null && !ended && (
                <ProgressBar value={elapsed} showValue={false} className="h-1rem border-round-3xl" color={ending ? "var(--orange-500)" : undefined} aria-label="بخش گذشته از دورهٔ اشتراک"/>
            )}
            {!status.canManage && (ending || ended) && <span className="text-xs text-500">برای تمدید با مدیر فضای کاری تماس بگیرید.</span>}
        </div>
        {action && <Button type="button" outlined={!ending && !ended} className="text-xs gap-2 flex-shrink-0 align-self-start md:align-self-center" icon="pi pi-credit-card" label={action}
                           onClick={() => navigate(status.managePath || "/settings?tab=subscriptions")}/>}
    </section>;
}
